import React, { FC, useMemo } from 'react';
import { Asset, Button, CheckmarkIcon } from '@clearcover/atlas';
import cx from 'classnames';

import { PaymentConcept } from './PaymentConcept';
import { PaymentPlanOptionProps, PaymentConceptName } from './types';
import styles from './styles.module.css';

export const PaymentPlanOption: FC<PaymentPlanOptionProps> = ({
  plan,
  selected,
  loading,
  breakdownSequence,
  onSelect,
}) => {
  // Create a map for quick lookup by name
  const breakdownMap = useMemo(() => {
    const map = new Map<PaymentConceptName, typeof plan.breakdown[number]>();

    for (const item of plan.breakdown) {
      map.set(item.name, item);
    }

    return map;
  }, [plan]);

  const breakdown = useMemo(() => {
    if (!breakdownSequence) return plan.breakdown;

    return breakdownSequence.map(key =>
      key === null ? null : breakdownMap.get(key) ?? null
    );
  }, [breakdownSequence, breakdownMap, plan.breakdown]);

  return (
    <div
      className={styles.paymentPlanOption}
      data-testid={`payment-plan-option-${plan.name}`}
    >
      <div
        className={cx(styles.paymentPlanContent, {
          [styles.selected]: selected,
        })}
      >
        <h4>{plan.displayName}</h4>

        <div className={styles.paymentBreakdown}>
          {breakdown.map((concept, idx) =>
            concept ? (
              <PaymentConcept
                key={concept.name}
                amount={concept.amount}
                label={concept.label}
                name={concept.name}
              />
            ) : (
              // Empty visual slot for empty entries
              <div
                key={`empty-${idx}`}
                className={cx(styles.emptySlot, styles.paymentConcept)}
                aria-hidden="true"
              />
            )
          )}
        </div>

        <Button
          type="button"
          size="sm"
          variant={selected ? 'secondary' : 'primary'}
          isSubmitting={selected && loading}
          disabled={selected || loading}
          onClick={onSelect}
        >
          {selected ? (
            <>
              <Asset
                icon={CheckmarkIcon}
                description="selected payment plan"
                width={16}
                height={16}
              />
              Selected
            </>
          ) : (
            'Choose'
          )}
        </Button>
      </div>
    </div>
  );
};
